// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';
import Tooltip from 'presentations/views/components/atoms/Tooltip';

import styled from '@emotion/styled';
import { useEventPopover } from './hooks';
import Popover, {
  PopoverPosition,
} from 'presentations/views/components/atoms/Popover';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import IconButton from 'presentations/views/components/atoms/IconButton';
import AttendeeAvatar from 'presentations/views/components/molecules/AttendeeAvatar';
import Icon from 'presentations/views/components/atoms/Icon';
import {
  black,
  blue01,
  gray01,
  gray02,
  gray04,
  gray05,
  gray06,
  paper,
  primary,
  white,
} from 'presentations/views/styles/color';
import { formatVideoServiceTypeName } from 'presentations/views/helpers/formatter';
import RichEditor from 'presentations/views/components/atoms/RichEditor';
import { isUrl } from 'infras/validator';
import { ViewerItem } from 'presentations/api/schema/user';
import { SourceType } from 'domains/models/event_source/sourceType';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Image from 'presentations/views/components/atoms/Image';
import ListLinkButton from 'presentations/views/components/molecules/ListLinkButton';
import Link from 'presentations/views/components/atoms/Link';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import {
  providerTypeIconPath,
  providerTypeName,
} from 'domains/models/calendar/providerType';
import EventMetaTagList from 'presentations/views/components/molecules/EventMetaTagList';
import UserEventTagList from 'presentations/views/components/molecules/UserEventTagList';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import CalendarColor from 'presentations/views/components/atoms/CalendarColor';
import {
  dashboardHostEventPath,
  eventTypePath,
} from 'presentations/routes/path';
import { UserAppItem } from 'presentations/api/schema/account';
import { IntegrationType } from 'domains/models/account/providerType';
import Progress from 'presentations/views/components/atoms/Progress';
import { useMemo } from 'react';
import MeetingButton from 'presentations/views/components/molecules/MeetingButton';
import { ResponseStatus } from 'domains/models/event/responseStatus';
import SwipeableDrawer from 'presentations/views/components/atoms/SwipableDrawer';
import { Status } from 'domains/models/event/status';
import { AttendType } from 'domains/models/event_type/attendType';

interface Props {
  viewer: ViewerItem;
  timeZone: string;
  mergedEvent: IntegrationMergedEventListItem;
  userId: string;
  onClose: Function;
  onEditEvent?: Function;
  onCancelledEvent?: Function;
  onChangeResponseStatus?: (responseStatus: ResponseStatus) => void;
  userApp: UserAppItem | null;
  element?: HTMLElement;
  position?: PopoverPosition;
  isPopover?: boolean;
  isDrawer?: boolean;
  linkTarget?: string;
}

export default function Component(props: Props) {
  const t = translateInComponent([translations]);

  const { state, actions } = useEventPopover(props);

  let displayStatus: any = undefined;
  if (state.displayType || state.visibilityType) {
    displayStatus = {
      displayType: state.displayType || undefined,
      visibilityType: state.visibilityType || undefined,
    };
  }

  const isJicooEvent =
    props.mergedEvent.calendarSourceType === CalendarSourceType.Jicoo;
  const isViewerEvent = state.eventUsers.some(
    (user) => user.uid === props.viewer.uid,
  );
  const isNotPoll = state.reservationMethod !== ReservationMethod.Poll;
  const isNotRequest = state.status !== Status.Request;
  const isEventTypeSource =
    state.jicooEvent && state.jicooEvent.sourceType === SourceType.EventType;
  const isNotIntegrationSource =
    state.jicooEvent &&
    state.jicooEvent.sourceType !== SourceType.IntegrationCalendar;

  const maxWidth = 320;
  const anchorOrigin = useMemo(() => {
    if (!props.element) {
      return { vertical: 0, horizontal: 0 };
    }
    const rect = props.element.getBoundingClientRect();
    const vertical: 'center' = 'center';
    let horizontal: 'right' | 'left' = 'right';
    if (window.innerWidth - rect.right < maxWidth) {
      horizontal = 'left';
    }
    return { vertical, horizontal };
  }, [props.element]);

  const renderContent = () => (
    <Container ref={state.containerRef} isPopover={props.isPopover}>
      <Header isPopover={props.isPopover}>
        <LeftHeader>
          <UserEventTagList
            isHost={
              state.jicooEvent?.attendType === AttendType.Agent || state.isHost
            }
            displayStatus={displayStatus}
            flexDirection="row"
            status={state.status}
          />
        </LeftHeader>
        <RightHeader>
          {isNotPoll && isNotRequest && (
            <>
              {state.jicooEvent?.isHost && !state.jicooEvent?.isSeminar && (
                <MenuIconTooltip title={t('Edit')}>
                  <StyledIconButton onClick={actions.onEditEvent}>
                    <Icon name="edit" size="20px" />
                  </StyledIconButton>
                </MenuIconTooltip>
              )}
              {isEventTypeSource &&
                !state.jicooEvent?.isSeminar &&
                state.isEnableGuestUpdate &&
                !state.detailEvent?.isBuffer && (
                  <MenuIconTooltip title={t('Reschedule')}>
                    <StyledIconButton onClick={actions.onRescheduleEvent}>
                      <Icon name="update" size="20px" />
                    </StyledIconButton>
                  </MenuIconTooltip>
                )}
            </>
          )}
          {isNotIntegrationSource && isNotRequest && (
            <>
              {isEventTypeSource &&
                state.isEnableGuestUpdate &&
                !state.detailEvent?.isBuffer && (
                  <MenuIconTooltip title={t('Cancel')}>
                    <StyledIconButton onClick={actions.onDeleteEvent}>
                      <Icon name="delete" size="20px" />
                    </StyledIconButton>
                  </MenuIconTooltip>
                )}
              {!isEventTypeSource && (
                <MenuIconTooltip title={t('Delete')}>
                  <StyledIconButton onClick={actions.onDeleteEvent}>
                    <Icon name="delete" size="20px" />
                  </StyledIconButton>
                </MenuIconTooltip>
              )}
            </>
          )}
          <StyledIconButton onClick={props.onClose}>
            <CloseIcon name="close" />
          </StyledIconButton>
        </RightHeader>
      </Header>
      <Content>
        <Title>{state.title}</Title>
        <DateTimeSection>
          <DateTime>{state.dateTimeString}</DateTime>
          {state.jicooEvent && (
            <MetaTagList>
              <EventMetaTagList event={state.jicooEvent} />
            </MetaTagList>
          )}
          <CalendarContainer>
            {/* <Calendar>
                  <CalendarColor color={props.event.eventType.color} />
                  <CalendarLink
                    target="_blank"
                    href={eventTypePath({
                      teamSlug: props.event.team.slug,
                      eventTypeSlug: props.event.eventType.slug,
                    })}
                  >
                    {props.event.eventType.name}
                  </CalendarLink>
                </Calendar> */}
            <Calendar>
              <CalendarColor color={state.calendarColor} />
              {state.detailEvent?.eventType && state.detailEvent.team ? (
                <CalendarLink
                  target="_blank"
                  href={eventTypePath({
                    teamSlug: state.detailEvent.team.slug,
                    eventTypeSlug: state.detailEvent.eventType.slug,
                  })}
                >
                  {state.detailEvent.eventType.name}
                </CalendarLink>
              ) : state.calendar ? (
                <CalendarName>{state.calendar.name}</CalendarName>
              ) : (
                <CalendarName>
                  {state.isLoading ? <Skeleton width={50} /> : 'Jicoo'}
                </CalendarName>
              )}
            </Calendar>
            {!!state.providerType &&
              state.isShowDetailLink &&
              isViewerEvent && (
                <EventNameIcon
                  src={providerTypeIconPath(state.providerType)}
                  alt={providerTypeName(state.providerType)}
                  width={20}
                  height={20}
                />
              )}
          </CalendarContainer>
          {state.isShowDetailLink ? (
            <EventLinkContainer>
              <EventLink href={state.eventDetailLink} target={props.linkTarget}>
                {t('Event Detail')}
              </EventLink>
            </EventLinkContainer>
          ) : (
            state.isLoading && (
              <EventLinkContainer>
                <EventLink>
                  <Skeleton variant="text" width={80} />
                </EventLink>
              </EventLinkContainer>
            )
          )}
        </DateTimeSection>
        {state.isBusy ? null : (
          <Section>
            {state.isShowLocation && (
              <DisplayItem>
                <DisplayItemTitle>{t('Location')}</DisplayItemTitle>
                <DisplayItemDetail>
                  {state.jicooEvent?.address && (
                    <LocationItem>
                      <a
                        href={`https://www.google.com/maps/place/${state.jicooEvent.address}`}
                        target="_blank"
                      >
                        {state.jicooEvent.address}
                      </a>
                    </LocationItem>
                  )}
                  {state.jicooEvent?.phoneNumber && (
                    <LocationItem>{state.jicooEvent.phoneNumber}</LocationItem>
                  )}
                  {isUrl(state.jicooEvent?.customLocation || '') ? (
                    <LocationItem>
                      <a
                        href={state.jicooEvent?.customLocation || undefined}
                        target="_blank"
                      >
                        {state.jicooEvent?.customLocation}
                      </a>
                    </LocationItem>
                  ) : (
                    state.jicooEvent?.customLocation && (
                      <LocationItem>
                        {state.jicooEvent.customLocation}
                      </LocationItem>
                    )
                  )}
                  {state.videoService && (
                    <LocationItem>
                      {formatVideoServiceTypeName(
                        state.videoService.videoServiceType,
                      )}
                    </LocationItem>
                  )}
                </DisplayItemDetail>
              </DisplayItem>
            )}
            {state.videoService && (
              <StyledMeetingButton
                videoServiceType={state.videoService.videoServiceType}
                url={state.videoService.url}
              />
            )}
            {isJicooEvent && (
              <DisplayItem>
                <DisplayItemTitle>{t('Page')}</DisplayItemTitle>
                <DisplayItemDetail>
                  {state.isLoading ? (
                    <Skeleton width={50} />
                  ) : (
                    <LocationItem>
                      {state.detailEvent?.page ? (
                        <Link
                          href={state.detailEvent?.page.url || undefined}
                          target="_blank"
                        >
                          {translate(
                            `IntegrationType_${state.detailEvent.page.integrationId}`,
                          )}
                        </Link>
                      ) : props.userApp?.isNotion &&
                        !state.jicooEvent?.routine ? (
                        <CreatePageContainer>
                          {state.isCreatingPage ? (
                            <Progress size={12} />
                          ) : (
                            <CreatePageButton
                              onClick={(e) =>
                                actions.onClickCreatePage(
                                  IntegrationType.Notion,
                                )
                              }
                            >
                              {t('Create Notion Page')}
                            </CreatePageButton>
                          )}
                        </CreatePageContainer>
                      ) : (
                        state.jicooEvent && (
                          <Link
                            href={dashboardHostEventPath(state.jicooEvent.uid, {
                              view: 'note',
                            })}
                          >
                            {'Jicoo'}
                          </Link>
                        )
                      )}
                    </LocationItem>
                  )}
                </DisplayItemDetail>
              </DisplayItem>
            )}
            {state.eventUsers.length > 0 && (
              <DisplayItemColumn>
                <DisplayItemTitle>{t('Attendees')}</DisplayItemTitle>
                <AttendeeList>
                  {state.eventUsers.map((user, index) => (
                    <AttendeeListItem key={index}>
                      <AttendeeAvatar
                        name={user.name}
                        profileImage={user.profileImage}
                        responseStatus={user.responseStatus}
                      />
                      <AttendeeUserName>
                        {user.name}
                        {(user.isOrganizer || user.isOptional) && (
                          <AttendeeOptionList>
                            {user.isOrganizer && (
                              <AttendeeOptionListItem>
                                {t('Organizer')}
                              </AttendeeOptionListItem>
                            )}
                            {user.isOptional && (
                              <AttendeeOptionListItem>
                                {t('Optional')}
                              </AttendeeOptionListItem>
                            )}
                          </AttendeeOptionList>
                        )}
                      </AttendeeUserName>
                    </AttendeeListItem>
                  ))}
                </AttendeeList>
              </DisplayItemColumn>
            )}
          </Section>
        )}
        {state.description && (
          <DescriptionSection>
            <RichEditor
              content={state.description}
              config={{
                formats: [
                  'header',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'list',
                  'link',
                ],
                toolbarContainer: false,
                readOnly: true,
              }}
            />
          </DescriptionSection>
        )}
      </Content>
      {!!state.integrationEvent && state.responseStatus !== null && (
        <Footer isPopover={props.isPopover}>
          <ResponseContainer>
            <ResponseLeftGroup>
              <ResponseButton
                label={t(`Response Status ${ResponseStatus.Tentative}`)}
                onClick={actions.onClickTenative}
                isActive={state.responseStatus === ResponseStatus.Tentative}
              ></ResponseButton>
            </ResponseLeftGroup>
            <ResponseRightGroup>
              <ResponseButton
                label={t(`Response Status ${ResponseStatus.Accepted}`)}
                onClick={actions.onClickAccept}
                isActive={state.responseStatus === ResponseStatus.Accepted}
              ></ResponseButton>
              <ResponseButton
                label={t(`Response Status ${ResponseStatus.Declined}`)}
                onClick={actions.onClickDecline}
                isActive={state.responseStatus === ResponseStatus.Declined}
              ></ResponseButton>
            </ResponseRightGroup>
          </ResponseContainer>
        </Footer>
      )}
      {state.confirmCancel && (
        <ConfirmationDialog
          isLoading={state.isCancelLoading}
          isOpen={!!state.confirmCancel}
          onSubmit={actions.onCancelEvent}
          submitLabel={translate('Yes')}
          onCancel={actions.onCloseCancelDialog}
          cancelLabel={translate('No')}
          title={state.cancelDialogTitle}
          text={state.cancelDialogText}
        />
      )}
    </Container>
  );

  return props.isDrawer ? (
    <SwipeableDrawer
      isOpen={!!props.element}
      onClose={props.onClose}
      // swipeAreaWidth={400}
      // isDisableSwipeToOpen={!isApple}
      isDisableSwipeToOpen
    >
      {renderContent()}
    </SwipeableDrawer>
  ) : props.isPopover ? (
    <Popover
      anchorReference={props.position && 'anchorPosition'}
      anchorPosition={props.position}
      anchor={props.element}
      anchorOrigin={anchorOrigin}
      transformOrigin={{
        vertical: anchorOrigin.vertical,
        horizontal: anchorOrigin.horizontal === 'right' ? 'left' : 'right',
      }}
      onClose={props.onClose}
      maxWidth={maxWidth}
    >
      {renderContent()}
    </Popover>
  ) : (
    renderContent()
  );
}

const Container = styled.div<{ isPopover?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  background-color: ${(props) => (props.isPopover ? paper : white)};
`;
const Header = styled.div<{ isPopover?: boolean }>`
  padding: ${spacing(8)} ${spacing(16)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  background-color: ${(props) => (props.isPopover ? paper : white)};
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;
const Content = styled.div`
  flex: 1;
  padding: ${spacing(8)} ${spacing(16)} ${spacing(16)};
`;
const Footer = styled.div<{ isPopover?: boolean }>`
  border-top: 1px solid ${gray04};
  padding: ${spacing(12)} ${spacing(16)};
  background-color: ${(props) => (props.isPopover ? paper : white)};
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const LeftHeader = styled.div`
  display: flex;
`;
const RightHeader = styled.div``;
const StyledIconButton = styled(IconButton)`
  padding: ${spacing(4)};
`;

const Title = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(4)};
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;
const DateTime = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(4)};
`;
const DateTimeSection = styled.div``;
const Section = styled.div`
  padding-top: ${spacing(12)};
  border-top: 1px solid ${gray04};
  margin-top: ${spacing(12)};
  :empty {
    display: none;
  }
`;
const DescriptionSection = styled.div`
  padding-top: ${spacing(12)};
  :empty {
    display: none;
  }

  .ql-editor {
    font-family: Helvetica, Arial, sans-serif;
    font-size: ${fontSize(14)};
    padding: 12px 0;
  }
  .ql-toolbar {
    border-top: 0px solid ${gray04};
    border-left: 0px solid ${gray04};
    border-right: 0px solid ${gray04};
  }
  .ql-container {
    margin: ${spacing(2)} 0 0 0;
    border-top: 1px solid ${gray04};
    border-bottom: 0px solid ${gray04};
    border-left: 0px solid ${gray04};
    border-right: 0px solid ${gray04};
  }
`;

const DisplayItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing(8)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const DisplayItemColumn = styled.div`
  margin-bottom: ${spacing(8)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const DisplayItemTitle = styled.div`
  margin-right: ${spacing(32)};
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  flex-shrink: 0;
`;
const DisplayItemDetail = styled.div`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const EventLinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const EventLink = styled(Link)`
  margin-top: ${spacing(16)};
  width: 100%;
  height: 36px;
  border-radius: ${borderRadius()};
  background-color: ${gray06};
  font-weight: ${fontWeight('bold')};
  color: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;
  :hover {
    background-color: ${blue01};
  }
`;

const EventNameIcon = styled(Image)`
  margin-right: ${spacing(4)};

  ${mediaMax.tablet} {
    width: 16px;
    height: 16px;
  }
`;

const AttendeeList = styled.div`
  margin-top: ${spacing(8)};
`;
const AttendeeListItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(12)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const AttendeeUserName = styled.span`
  margin-left: ${spacing(8)};
  max-width: 280px;
`;
const MetaTagList = styled.div`
  margin-top: ${spacing(8)};
`;
const CalendarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Calendar = styled.div`
  display: flex;
  align-items: center;
`;
const CalendarName = styled.div`
  font-size: ${fontSize(13)};
`;
const CalendarLink = styled(Link)`
  color: ${black};
`;

const MenuIconTooltip = styled(Tooltip)`
  margin-right: ${spacing(8)};
`;
const LocationItem = styled.span`
  margin-right: ${spacing(8)};
  :last-of-type {
    margin-right: 0;
  }
`;

const CreatePageContainer = styled.div``;
const CreatePageButton = styled.div`
  cursor: pointer;
  color: ${primary};
`;
const StyledMeetingButton = styled(MeetingButton)`
  margin: ${spacing(12)} 0 ${spacing(20)};
`;

const ResponseContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
const ResponseLeftGroup = styled.div`
  display: flex;
  align-items: center;
`;
const ResponseRightGroup = styled.div`
  display: flex;
  align-items: center;
`;
const ResponseButton = styled(ListLinkButton)`
  height: auto;
  width: 80px;
  padding: ${spacing(4)};
  margin-right: ${spacing(8)};
  background-color: ${(props) => (props.isActive ? blue01 : gray05)};
  color: ${(props) => (props.isActive ? primary : gray01)};
  :last-of-type {
    margin-right: 0;
  }
`;
const AttendeeOptionList = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(2)};
`;
const AttendeeOptionListItem = styled.div`
  font-size: ${fontSize(12)};
  color: ${gray02};
  line-height: 1;
  margin-right: ${spacing(8)};
  :last-of-type {
    margin-right: 0;
  }
`;

const CloseIcon = styled(Icon)`
  width: 28px !important;
  height: 28px !important;
`;
